import {
  FC, type JSX, type ReactNode, useMemo, useState,
} from 'react';
import { useDebouncedValue } from 'src/hooks/useDebouncedValue';

import { EmptyState } from 'components/EmptyState';

import { FilterAndSearchRow } from './FilterAndSearchRow';
import { SearchableTableContent } from './SearchableTableContent';
import { ActiveFilters, ColumnDefinition, FilterGroup, SortState } from './types';

import classes from './style.module.css';

// Debounce the search query before running the filter/search/sort pipeline so a
// large table doesn't re-run the whole pass on every keystroke. Keeps the input
// itself responsive (it stays controlled on the raw query). Imperceptible for
// small tables; the lever that keeps big ones usable.
const DEFAULT_SEARCH_DEBOUNCE_MS = 150;

// Stable reference for the no-filters case. Using a fresh `[]` default param
// would change identity every render, busting the processedData memo (and the
// debounce) so the whole filter/search/sort pipeline re-ran on every keystroke.
const EMPTY_FILTER_GROUPS: FilterGroup<unknown>[] = [];

type SearchableTableProps<T> = {
  initialData: T[];
  searchFunction: (query: string, data: T) => boolean;
  rowComponent: (item: T) => JSX.Element;
  columns: ColumnDefinition<T>[];
  filterGroups?: FilterGroup<T>[];
  hideSearch?: boolean; // hide search bar
  useDropdown?: boolean; // Whether to use dropdown instead of filter buttons
  useChipFilters?: boolean; // Whether to use chip-style filters ("+ Add filter" + chips)
  initialSortState?: SortState;
  // Row virtualization. 'auto' (default) virtualizes only above a row-count
  // threshold so small tables are unaffected; `true`/`false` force it on/off.
  virtualize?: 'auto' | boolean;
  // Placeholder shown when the table has no data at all (before any search or
  // filter). When a search/filter is active but matches nothing, a generic
  // "No results found" message is shown instead, regardless of this.
  emptyState?: {
    title?: string;
    message?: ReactNode;
    icon?: ReactNode;
    action?: ReactNode;
  };
  // Debounce (ms) applied to the search query before the pipeline runs. 0
  // disables debouncing (run on every keystroke). Defaults to a small delay.
  searchDebounceMs?: number;
};

// Filter, search, and sort helper function
function filterSearchAndSort<T>(
  data: T[],
  groups: FilterGroup<T>[],
  filters: ActiveFilters,
  searchQuery: string,
  searchFunction: (query: string, data: T) => boolean,
  sortState: SortState,
  columns: ColumnDefinition<T>[],
) {
  const filteredData = data.filter((item) => {
    return Object.keys(filters).every((groupId) => {
      const currentGroup = groups.find((f) => f.groupId === groupId);
      const currentFilter = currentGroup?.filters.find((f) => f.name === filters[groupId]);
      return currentFilter ? currentFilter.applyFilterFunction(item) : true;
    });
  });

  const searchedData = filteredData.filter((item) => searchFunction(searchQuery, item));

  if (sortState.columnName && sortState.direction) {
    const column = columns.find(col => col.name === sortState.columnName);
    if (column?.sortFunction) {
      const sorted = [...searchedData].sort(column.sortFunction);
      return sortState.direction === 'desc' ? sorted.reverse() : sorted;
    }
  }

  return searchedData;
}

const SearchableTable: FC<SearchableTableProps<any>> = ({
  initialData,
  searchFunction,
  rowComponent,
  columns,
  filterGroups = EMPTY_FILTER_GROUPS,
  hideSearch = false,
  useDropdown = false,
  useChipFilters = false,
  initialSortState,
  virtualize = 'auto',
  emptyState,
  searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
}) => {
  // State for search query. `query` drives the (responsive) input; the pipeline
  // runs on the debounced value so big tables don't re-filter on every keystroke.
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, searchDebounceMs);

  // State for active filters. In chip mode, start empty (no filters applied);
  // otherwise seed with each group's first option to match legacy behavior.
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(() => filterGroups.reduce(
    (acc: ActiveFilters, { groupId, filters }) => {
      acc[groupId] = useChipFilters ? null : (filters[0]?.name || null);
      return acc;
    },
    {},
  ));

  // State for sorting
  const [sortState, setSortState] = useState<SortState>(
    initialSortState ?? { columnName: null, direction: null },
  );

  // Update active filters when a filter is selected or cleared (chip × button).
  const handleFilterChange = (groupId: string, filterName: string | null) => {
    setActiveFilters((prevFilters) => ({ ...prevFilters, [groupId]: filterName }));
  };

  // Handle sorting
  const handleSort = (columnName: string) => {
    setSortState(prevState => {
      if (prevState.columnName === columnName) {
        // Cycle through: asc -> desc -> null
        const newDirection = prevState.direction === 'asc' ? 'desc' : 
                           prevState.direction === 'desc' ? null : 'asc';
        return {
          columnName: newDirection ? columnName : null,
          direction: newDirection,
        };
      } else {
        // New column, start with asc
        return {
          columnName,
          direction: 'asc',
        };
      }
    });
  };

  // Filtered, searched, and sorted data for the table
  const processedData = useMemo(
    () => filterSearchAndSort(initialData, filterGroups, activeFilters, debouncedQuery, searchFunction, sortState, columns),
    [initialData, filterGroups, activeFilters, debouncedQuery, searchFunction, sortState, columns],
  );

  // Whether the user has narrowed the table — used to pick the right empty
  // message: "no matches for your search" vs "this table has no data yet".
  const isSearchActive = query.trim() !== ''
    || Object.values(activeFilters).some((value) => value != null);
  const isEmpty = processedData.length === 0;

  return (
    <div className="flex flex-col">
      <FilterAndSearchRow
        filterGroups={filterGroups}
        activeFilters={activeFilters}
        handleFilterChange={handleFilterChange}
        query={query}
        setQuery={setQuery}
        hideSearch={hideSearch}
        useDropdown={useDropdown}
        useChipFilters={useChipFilters}
      />
      {isEmpty ? (
        isSearchActive ? (
          <EmptyState
            className={classes.emptyStateContainer}
            title="No results found"
            message="Try adjusting your search terms"
          />
        ) : (
          <EmptyState
            className={classes.emptyStateContainer}
            title={emptyState?.title}
            message={emptyState?.message}
            icon={emptyState?.icon}
            action={emptyState?.action}
          />
        )
      ) : (
        <SearchableTableContent
          filteredData={processedData}
          rowComponent={rowComponent}
          columns={columns}
          sortState={sortState}
          onSort={handleSort}
          virtualize={virtualize}
        />
      )}
    </div>
  );
};

export default SearchableTable;
